import React, { useState } from 'react';

import ChevronDown from '@gravity-ui/icons/ChevronDown';
import Plus from '@gravity-ui/icons/Plus';
import Xmark from '@gravity-ui/icons/Xmark';
import { Button, Checkbox, Icon, Select, TextArea, TextInput, withToaster } from '@gravity-ui/uikit';
import i18n from '@yandex-int/i18n';
import { updateDraft } from 'client/api';
import { AliceSkillControlPanel } from 'client/blocks/ControlPanel/AliceSkillControlPanel';
import {
    ActivationPhrasesDescription,
    AppMetricaDesctiptionBanner,
    BrandVerificationWebsiteDescription,
    RsyFieldDescription,
    SkillNameDescription,
    StateFlagDescription,
} from 'client/blocks/descriptions/descriptions';
import { DevPreviewBanner } from 'client/blocks/DevPreviewBanner/DevPreviewBanner';
import { ErrorBoundary } from 'client/blocks/ErrorBoundary/ErrorBoundary';
import { AliceSkillLogoUploader } from 'client/blocks/FilesUploaders/AliceSkillLogoUploader';
import { BackendTypeField } from 'client/blocks/form/BackendTypeField/BackendTypeField';
import { EmailField } from 'client/blocks/form/EmailField/EmailField';
import { ExamplesBuilder } from 'client/blocks/form/ExamplesBuilder/ExamplesBuilder';
import { Field } from 'client/blocks/form/Field/Field';
import { FieldList } from 'client/blocks/form/FieldList/FieldList';
import { FormCard } from 'client/blocks/form/FormCard/FormCard';
import { HostsSelect } from 'client/blocks/form/HostsSelect/HostsSelect';
import { InstallableAppAccessField } from 'client/blocks/form/InstallableAppAccessField/InstallableAppAccessField';
import { InterfaceSelector } from 'client/blocks/form/InterfaceSelector/InterfaceSelector';
import { SelectAudio } from 'client/blocks/form/SelectAudio/SelectAudio';
import { TextInputWithCommonness } from 'client/blocks/form/TextInputWithCommonness/TextInputWithCommonness';
import { SkillUserAgreement } from 'client/blocks/SkillUserAgreement/SkillUserAgreement';
import { ModeratorNotes } from 'client/blocks/statuses/Notes/ModeratorNotes';
import { WithSnapshot, withSnapshot } from 'client/context/snapshotContext';
import formContainer, { FormContainerProps } from 'client/hocs/form-container';
import { FormErrors, FormState, InjectedFormProps } from 'client/hocs/types';
import {
    lengthConfig,
    validateActivationPhrase,
    validateDescription,
    validateDeveloperName,
    validateEmail,
    validateName,
    validateNote,
    validateRsyPlatformId,
    validateStructuredExample,
    validateURL,
} from 'client/lib/validation';
import { Example } from 'client/model/common';
import { AliceSkillSettings, SkillAccess } from 'client/model/settings';
import { AliceSkill, Channel } from 'client/model/skill';
import { formUARequests, UserAgreement } from 'client/model/userAgreement';
import { AliceSkillFormSettings } from 'client/types';
import { displayUserAgreements, getCanEditApp, getCanViewAsAdmin } from 'client/utils';
import { createUpdateError } from 'client/utils/errorMessage';
import { mapSkillSettingsToValues, mapValuesToSkillSettings } from 'client/utils/settings/alice-skill';
import { removeDuplicates } from 'client/utils/settings/common';
import { getSettingsLabels } from 'client/utils/settings/labels';
import { addToast } from 'client/utils/settings/toaster';
import * as utils from 'client/utils/settings/utils';
import * as aliceSkillFormKeyset from 'i18n/AliceSkillForm';
import * as keyset from 'i18n/AppForm';
import * as TvAppIndexKeyset from 'i18n/AppsIndex';
import * as appsIndexKeyset from 'i18n/AppsIndex';
import * as draftControlKeyset from 'i18n/DraftControlPanel';
import * as generalSettingsKeyset from 'i18n/GeneralSettings';
import * as settingsErrorsKeyset from 'i18n/SettingsErrors';
import * as settingLabelsKeyset from 'i18n/SettingsLabels';
import * as smartHomeKeyset from 'i18n/SmartHomeForm';
import isEqual from 'lodash/isEqual';

import styles from './Settings.module.scss';

const t = i18n(keyset);
const tAliceSkill = i18n(aliceSkillFormKeyset);
const tAppsIndex = i18n(appsIndexKeyset);
const tDraftControl = i18n(draftControlKeyset);
const tGeneralSettings = i18n(generalSettingsKeyset);
const tSettingsErrors = i18n(settingsErrorsKeyset);
const tSettingLabels = i18n(settingLabelsKeyset);
const tSmartHome = i18n(smartHomeKeyset);
const tTvAppIndex = i18n(TvAppIndexKeyset);

interface OuterProps extends WithSnapshot {
    brandIsVerified?: boolean;
    disabled: boolean;
    displayEmailVerificationError: boolean;
    displayBrandVerificationError?: boolean;
    handleSaveDraft?: () => Promise<void>;
    isDraft: boolean;
    isAccountLinkingTemplate: boolean;
    skillUseZora: boolean;
    logoColor: string;
    settings: AliceSkillSettings;
    skill: AliceSkill;
    refreshAliceApp?: () => void;
    fetchedUserAgreements?: UserAgreement[];
}

type Props = InjectedFormProps<AliceSkillFormSettings, OuterProps>;

const AliceForm: React.FC<Props> = ({
    brandIsVerified,
    displayBrandVerificationError,
    displayEmailVerificationError,
    disabled,
    errors,
    handleSaveDraft = async () => {},
    isDraft,
    isNonValidationError,
    isSubmitting,
    onChange,
    onCheckboxChange,
    setStateAndValidate,
    setErrors,
    skill,
    snapshot,
    touched,
    values,
}) => {
    const [openCardName, setOpenCardName] = useState('');

    const l = getSettingsLabels()[Channel.AliceSkill];

    const fromDisabled = disabled || isSubmitting || !getCanEditApp(skill);

    const isPrivate = values.skillAccess === SkillAccess.Private;

    const { voices } = snapshot;
    const categories =
        snapshot.categories[0].type === 'smart_home' ? snapshot.categories.slice(1) : snapshot.categories;

    const validateAsAdmin = utils.validateAsAdmin(getCanViewAsAdmin(), skill.developerType);

    const displayExamplesBuilder = values.activationPhrases.filter(Boolean).length > 0;

    const hideCommonness = brandIsVerified || isPrivate;

    const handleMobileCardClick = (tabName: string) => {
        if (tabName === openCardName) {
            setOpenCardName('');
        } else {
            setOpenCardName(tabName);
        }
    };

    const handleChangeActivationPhrases = (activationPhrases: string[]) => {
        const changedValues: AliceSkillFormSettings = {
            ...values,
            activationPhrases,
            structuredExamples: utils.buildStructuredExamples(
                values.structuredExamples,
                values.name,
                activationPhrases,
            ),
        };

        const changedErorrs: FormErrors<AliceSkillFormSettings> = {
            ...errors,
            structuredExamples: changedValues.structuredExamples.map((value, i) => {
                const oldError = errors.structuredExamples ? errors.structuredExamples[i] : '';

                return validateStructuredExample(value, activationPhrases) || oldError;
            }),
        };

        setStateAndValidate(
            {
                values: changedValues,
                errors: changedErorrs,
            },
            ['activationPhrases'],
        );
    };

    return (
        <ErrorBoundary withHomepageLink>
            <div>
                <div className={styles.header}>
                    <AliceSkillControlPanel
                        settingValues={values}
                        isSubmitting={isSubmitting}
                        onSettingValuesChange={onChange}
                        settingFormErrors={errors}
                        handleSaveDraft={handleSaveDraft}
                        setErrors={setErrors}
                        isNonValidationError={isNonValidationError}
                        appName={skill.onAir ? skill.name : skill.draft.name}
                        logoUrl={skill.draft.logo2?.url || ''}
                    />
                    {isDraft && <ModeratorNotes />}
                </div>
                <div className={styles.mobileFormHeader}>
                    <ChevronDown />
                    {tTvAppIndex('main-information')}
                </div>
                <FormCard
                    cardKey="about"
                    cardTitle={tAppsIndex('about-skill')}
                    handleOpenCard={handleMobileCardClick}
                    openCardName={openCardName}
                >
                    <Field
                        required
                        fieldTitle={tAliceSkill('skill-name')}
                        fieldDescription={<SkillNameDescription />}
                        error={errors.name}
                    >
                        <TextInputWithCommonness
                            className={styles.formElement}
                            value={values.name}
                            onUpdate={(name) => {
                                const newActivationPhrases = [name, ...values.activationPhrases.slice(1)];
                                const changedValues: AliceSkillFormSettings = {
                                    ...values,
                                    name,
                                    activationPhrases: newActivationPhrases,
                                    structuredExamples: utils.buildStructuredExamples(
                                        values.structuredExamples,
                                        name,
                                        newActivationPhrases,
                                    ),
                                };

                                const changedErorrs: FormErrors<AliceSkillFormSettings> = {
                                    ...errors,
                                    structuredExamples: changedValues.structuredExamples.map((value, i) => {
                                        const oldError = errors.structuredExamples ? errors.structuredExamples[i] : '';

                                        return validateStructuredExample(value, values.activationPhrases) || oldError;
                                    }),
                                };

                                setStateAndValidate(
                                    {
                                        values: changedValues,
                                        errors: changedErorrs,
                                    },
                                    ['name', 'activationPhrases'],
                                );
                            }}
                            placeholder={t('app-name-placeholder')}
                            hideCommonness={hideCommonness}
                            disabled={fromDisabled}
                        />
                    </Field>
                    <Field
                        fieldTitle={tSettingLabels('activation-phrases')}
                        fieldDescription={<ActivationPhrasesDescription />}
                    >
                        {values.activationPhrases.map((phraseText, phraseIndex) => (
                            <TextInput
                                key={phraseIndex}
                                className={styles.formElement}
                                value={phraseText}
                                onChange={(e) => {
                                    const copiedPhrases = [...values.activationPhrases];
                                    copiedPhrases[phraseIndex] = e.target.value;
                                    handleChangeActivationPhrases(copiedPhrases);
                                }}
                                error={!!errors.activationPhrases?.[phraseIndex]}
                                errorMessage={errors.activationPhrases?.[phraseIndex]}
                                endContent={
                                    phraseIndex > 0 ? (
                                        <Button
                                            onClick={() => {
                                                const updatedPhrases = [...values.activationPhrases];
                                                updatedPhrases.splice(phraseIndex, 1);
                                                onChange('activationPhrases')(updatedPhrases);
                                            }}
                                            view="flat"
                                            disabled={fromDisabled}
                                        >
                                            <Icon data={Xmark} />
                                        </Button>
                                    ) : undefined
                                }
                                size="xl"
                                disabled={fromDisabled || phraseIndex === 0}
                            />
                        ))}
                        <Button
                            className={styles.formButton}
                            onClick={() => onChange('activationPhrases')([...values.activationPhrases, ''])}
                            disabled={fromDisabled || (!validateAsAdmin && values.activationPhrases?.length >= 4)}
                        >
                            <Icon data={Plus} />
                            {t('add')}
                        </Button>
                    </Field>

                    {displayExamplesBuilder && (
                        <ExamplesBuilder
                            values={values.structuredExamples}
                            onChange={onChange('structuredExamples')}
                            errors={errors.structuredExamples}
                            activationPhrasesOptions={removeDuplicates(
                                [values.name, ...values.activationPhrases].filter(Boolean),
                            )}
                            disabled={fromDisabled}
                        />
                    )}

                    <Field required fieldTitle={t('description-title')} fieldDescription={t('description-description')}>
                        <TextArea
                            className={styles.formElement}
                            value={values.description}
                            onChange={(e) => onChange('description')(e.target.value)}
                            error={errors.description}
                            note={
                                <span className={styles.grayNote}>
                                    {lengthConfig.description - (values.description?.length || 0)}
                                </span>
                            }
                            rows={4}
                            size="xl"
                            disabled={fromDisabled}
                        />
                    </Field>

                    <Field required fieldTitle={l.voice}>
                        <SelectAudio
                            className={styles.formElement}
                            value={values.voice}
                            onChange={onChange('voice')}
                            voices={voices}
                            placeholder={tSettingLabels('voice')}
                            disabled={fromDisabled}
                        />
                    </Field>

                    <BackendTypeField
                        skill={skill}
                        values={values}
                        onBackendTypeChange={onChange('backendType')}
                        onFunctionIdChange={onChange('functionId')}
                        onWebhookChange={onChange('uri')}
                        errors={errors}
                        disabled={fromDisabled}
                    />

                    <Field fieldTitle={l.useStateStorage} bottomNote={<StateFlagDescription />}>
                        <Checkbox
                            className={styles.formElement}
                            checked={values.useStateStorage}
                            onUpdate={onCheckboxChange('useStateStorage')}
                            size="l"
                            disabled={fromDisabled}
                        >
                            {tAliceSkill('storage-option')}
                        </Checkbox>
                    </Field>

                    <InstallableAppAccessField
                        installableAppAccess={values.skillAccess}
                        setInstallableAppAccess={onChange('skillAccess')}
                        channel={skill.channel}
                        disabled={fromDisabled}
                    />

                    <Field required fieldTitle={l.category}>
                        <Select
                            className={styles.formElement}
                            value={[values.category]}
                            onUpdate={(value) => {
                                onChange('category')(value?.[0]);
                            }}
                            options={categories.map(({ type, title }) => ({ value: type, content: title }))}
                            placeholder={t('select-category')}
                            size="xl"
                            disabled={fromDisabled}
                        />
                    </Field>

                    <Field fieldTitle={l.explicitContent}>
                        <Checkbox
                            className={styles.formElement}
                            checked={values.explicitContent}
                            onUpdate={onCheckboxChange('explicitContent')}
                            size="l"
                            disabled={fromDisabled}
                        >
                            {tAliceSkill('age-option-adult')}
                        </Checkbox>
                    </Field>

                    <Field required fieldTitle={l.developerName} error={errors.developerName}>
                        <TextInput
                            className={styles.formElement}
                            value={values.developerName}
                            onChange={(e) => onChange('developerName')(e.target.value)}
                            size="xl"
                            disabled={fromDisabled}
                        />
                    </Field>

                    <EmailField
                        value={values.email}
                        onChange={onChange('email')}
                        error={
                            errors.email || (displayEmailVerificationError ? tSmartHome('not-confirmed') : undefined)
                        }
                        touched={touched.email}
                        isVerified={!displayEmailVerificationError && !errors.email}
                        disabled={fromDisabled}
                    />

                    <Field
                        required
                        fieldTitle={l.logoId}
                        fieldDescription={t('icon-description')}
                        error={errors.logoId}
                    >
                        <AliceSkillLogoUploader
                            logoUrl={values.logoUrl}
                            onIdChange={onChange('logoId')}
                            onUrlChange={onChange('logoUrl')}
                            disabled={fromDisabled}
                            setErrors={setErrors}
                        />
                    </Field>

                    <Field
                        fieldTitle={l.brandVerificationWebsite}
                        fieldDescription={<BrandVerificationWebsiteDescription />}
                        error={displayBrandVerificationError ? tSettingLabels('not-verified') : undefined}
                    >
                        <HostsSelect
                            value={values.brandVerificationWebsite}
                            onChange={onChange('brandVerificationWebsite')}
                            error={errors.brandVerificationWebsite}
                            disabled={fromDisabled}
                        />
                    </Field>

                    <InterfaceSelector
                        values={values.requiredInterfaces}
                        onChange={onChange('requiredInterfaces')}
                        label={l.requiredInterfaces}
                        disabled={fromDisabled}
                    />

                    {__appConfig.user.isAdmin && (
                        <>
                            <FieldList
                                values={values.exactSurfaces}
                                onChange={onChange('exactSurfaces')}
                                errors={errors.exactSurfaces}
                                label={l.exactSurfaces}
                                disabled={fromDisabled}
                            />
                            <FieldList
                                values={values.surfaceWhitelist}
                                onChange={onChange('surfaceWhitelist')}
                                errors={errors.surfaceWhitelist}
                                label={l.surfaceWhitelist}
                                disabled={fromDisabled}
                            />
                            <FieldList
                                values={values.surfaceBlacklist}
                                onChange={onChange('surfaceBlacklist')}
                                errors={errors.surfaceBlacklist}
                                label={l.surfaceBlacklist}
                                disabled={fromDisabled}
                            />
                        </>
                    )}
                </FormCard>

                {displayUserAgreements() && (
                    <FormCard
                        cardKey="user-agreements"
                        cardTitle={t('agreement')}
                        handleOpenCard={handleMobileCardClick}
                        openCardName={openCardName}
                    >
                        <SkillUserAgreement
                            agreements={values.skillUserAgreements}
                            onChange={onChange('skillUserAgreements')}
                            disabled={fromDisabled}
                        />
                    </FormCard>
                )}

                <FormCard
                    cardKey="others"
                    cardTitle={tDraftControl('others')}
                    handleOpenCard={handleMobileCardClick}
                    openCardName={openCardName}
                >
                    <Field
                        fieldTitle={tAppsIndex('app-metrica-settings')}
                        fieldDescription={<AppMetricaDesctiptionBanner />}
                    >
                        <TextInput
                            className={styles.formElement}
                            value={values.appMetricaApiKey}
                            onChange={(e) => onChange('appMetricaApiKey')(e.target.value)}
                            size="xl"
                            disabled={fromDisabled}
                        />
                        <DevPreviewBanner />
                    </Field>
                    <Field fieldTitle={tAliceSkill('label-rtb-id')} bottomNote={<RsyFieldDescription />}>
                        <TextInput
                            className={styles.formElement}
                            value={values.rsyPlatformId}
                            onChange={(e) => onChange('rsyPlatformId')(e.target.value)}
                            size="xl"
                            disabled={fromDisabled}
                        />
                    </Field>
                </FormCard>
            </div>
        </ErrorBoundary>
    );
};

const AliceSkillForm = formContainer<OuterProps, AliceSkillFormSettings>({
    mapPropsToValues: (props) => mapSkillSettingsToValues(props.settings),
    handleSubmit: async (values, props, { setErrors }) => {
        try {
            const settings = mapValuesToSkillSettings(values);

            await updateDraft(props.skill.id, settings, props.skill.channel);
            addToast(props, { title: tGeneralSettings('changes-save-success'), theme: 'success' });

            if (!isEqual(props.fetchedUserAgreements, values.skillUserAgreements)) {
                await formUARequests(
                    props.skill.id,
                    props.fetchedUserAgreements ?? [],
                    values.skillUserAgreements,
                    props.skill.channel,
                );
            }
            if (props.refreshAliceApp) {
                await props.refreshAliceApp();
            }
            await props.refreshSnapshot();
        } catch (error) {
            await new Promise((resolve) => setErrors(createUpdateError(error, props.skill.channel), resolve));
            addToast(props, { title: tGeneralSettings('error'), theme: 'danger' });
            console.error(error);
            throw error;
        }
    },
    setupValidationSchema: (props) => {
        const validateAsAdmin = utils.validateAsAdmin(__appConfig.user.isAdmin, props.skill.developerType);

        return {
            structuredExamples: (value: Example[], formState: FormState<AliceSkillFormSettings>) => ({
                structuredExamples: value.map((example) =>
                    validateStructuredExample(example, formState.values.activationPhrases),
                ),
            }),
            activationPhrases: (value: string[]) => ({
                activationPhrases: value.map((v) =>
                    validateActivationPhrase(v, validateAsAdmin, 'field-activation-name'),
                ),
            }),
            developerName: (value: string) => ({
                developerName: validateDeveloperName(value, validateAsAdmin),
            }),
            name: (value: string) => ({
                name: value
                    ? validateName(value, validateAsAdmin, 'field-skill-name')
                    : tSettingsErrors('name-is-required'),
            }),
            uri: (value: string) => ({
                uri: validateURL(value, props.skillUseZora),
            }),
            brandVerificationWebsite: (value: string) => ({
                brandVerificationWebsite: validateURL(value),
            }),
            description: (value: string) => ({
                description: validateDescription(value),
            }),
            noteForModerator: (value: string) => ({
                noteForModerator: validateNote(value),
            }),
            email: (value: string) => ({
                email: validateEmail(value),
            }),
            rsyPlatformId: (value: string) => ({
                rsyPlatformId: validateRsyPlatformId(value),
            }),
        };
    },
})(AliceForm);

const AliceSkillFormWithToaster = withToaster<OuterProps & FormContainerProps>()(AliceSkillForm);

export default withSnapshot(AliceSkillFormWithToaster);
